export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (html !== undefined) node.innerHTML = html;
  return node;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Nederlandse notatie: komma als decimaalteken. */
export function nl(value: number, decimals = 1): string {
  return value.toLocaleString('nl-BE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Temperatuur met een echt minteken in plaats van een koppelteken. */
export function temp(value: number, decimals = 1): string {
  return nl(value, decimals).replace('-', '−');
}

const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const MONTHS_LONG = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

export const MONTH_LABELS = MONTHS;

export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d ? `${d}/${m}/${y}` : iso;
}

export function formatDateShort(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return m && d ? `${d} ${MONTHS[m - 1]}` : iso;
}

export function formatDateLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return y && m && d ? `${d} ${MONTHS[m - 1]} ${y}` : iso;
}

export function formatDateFull(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return y && m && d ? `${d} ${MONTHS_LONG[m - 1]} ${y}` : iso;
}

/**
 * Hoe lang geleden iets gebeurde, in woorden.
 *
 * Bewust geen maandnaam: die staat er in de kaart al naast, en "in juni 2026"
 * naast "8 jun 2026 – 12 jun 2026" voegt niets toe. Een afstand wel.
 */
export function agoLabel(days: number): string {
  if (days <= 0) return 'vandaag nog';
  if (days === 1) return 'gisteren';
  if (days < 45) return `${days} dagen geleden`;
  if (days < 365) return `${Math.round(days / 30)} maanden geleden`;
  // Naar beneden afronden: "3 jaar geleden" voor iets van tweeënhalf jaar oud
  // klinkt preciezer dan het is.
  const years = Math.floor(days / 365);
  return years === 1 ? 'een jaar geleden' : `${years} jaar geleden`;
}

/** Lokale datum als YYYY-MM-DD; `toISOString()` zou hier UTC geven. */
export function ymd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatTime(value: string | number | null): string | null {
  if (value === null) return null;
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
}

/** Dag-van-jaar → korte datum. 2001 is geen schrikkeljaar. */
export function dayLabel(day: number): string {
  const date = new Date(Date.UTC(2001, 0, day));
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

export type TableSpec = { columns: string[]; rows: (string | number | null)[][] };

/**
 * Sectiekaart met kop, optionele bediening en een tabelweergave.
 *
 * De tabel is geen extraatje: drie van de zeven jaarkleuren zitten onder de
 * contrastdrempel op dit lichte vlak, en een tooltip mag nooit de enige weg naar
 * een waarde zijn.
 */
export function panel(options: {
  eyebrow: string;
  title: string;
  note?: string;
  legend?: { color?: string; gradient?: string; label: string; kind?: 'line' | 'dash' | 'block' | 'ring' }[];
  head?: HTMLElement[];
  withTable?: boolean;
}): {
  root: HTMLElement;
  body: HTMLElement;
  left: HTMLElement;
  setTable: (spec: TableSpec) => void;
  setNote: (text: string) => void;
} {
  const root = el('section', 'panel rise');

  const head = el('div', 'panel-head');
  const left = el('div');
  left.append(el('div', 'eyebrow', escapeHtml(options.eyebrow)));
  left.append(el('h2', undefined, escapeHtml(options.title)));
  const note = el('div', 'note');
  if (options.note) {
    note.textContent = options.note;
    left.append(note);
  }
  head.append(left);

  const right = el('div', 'legend');
  for (const item of options.head ?? []) right.append(item);
  for (const item of options.legend ?? []) {
    const style =
      item.kind === 'dash'
        ? `border-top-color:${item.color ?? ''}`
        : item.gradient
          ? `background:${item.gradient}`
          : `background:${item.color ?? ''}`;
    right.append(
      el(
        'span',
        'k',
        `<i class="${item.kind === 'dash' ? 'dash' : item.kind === 'block' ? 'block' : item.kind === 'ring' ? 'ring' : ''}" style="${style}"></i>${escapeHtml(item.label)}`,
      ),
    );
  }

  const tableView = el('div', 'table-view hidden');
  const body = el('div');

  if (options.withTable) {
    const toggle = el('button', 'table-toggle', 'Tabel');
    toggle.type = 'button';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.addEventListener('click', () => {
      const show = tableView.classList.contains('hidden');
      tableView.classList.toggle('hidden', !show);
      body.classList.toggle('hidden', show);
      toggle.setAttribute('aria-expanded', String(show));
      toggle.textContent = show ? 'Grafiek' : 'Tabel';
    });
    right.append(toggle);
  }

  head.append(right);
  root.append(head, body, tableView);

  return {
    root,
    body,
    left,
    setNote(text) {
      note.textContent = text;
      if (!note.isConnected) left.append(note);
    },
    setTable(spec) {
      const thead = spec.columns.map((c) => `<th scope="col">${escapeHtml(c)}</th>`).join('');
      const tbody = spec.rows
        .map(
          (row) =>
            `<tr>${row
              .map((cell) => `<td>${cell === null || cell === undefined ? '—' : escapeHtml(String(cell))}</td>`)
              .join('')}</tr>`,
        )
        .join('');
      tableView.innerHTML = `<table><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table>`;
    },
  };
}

/** Rij pilknoppen; precies één is actief. */
export function chipRow(
  options: { key: string; label: string }[],
  current: string,
  onPick: (key: string) => void,
  className = 'chips',
): HTMLElement {
  const wrap = el('div', className);
  for (const option of options) {
    const button = el('button', 'chip', escapeHtml(option.label));
    button.type = 'button';
    button.setAttribute('aria-pressed', String(option.key === current));
    button.classList.toggle('on', option.key === current);
    button.addEventListener('click', () => onPick(option.key));
    wrap.append(button);
  }
  return wrap;
}

export function select(
  label: string,
  options: { value: string; label: string }[],
  current: string,
  onChange: (value: string) => void,
): HTMLElement {
  const wrap = el('label');
  wrap.append(document.createTextNode(label));
  const node = el('select');
  for (const option of options) {
    const opt = el('option');
    opt.value = option.value;
    opt.textContent = option.label;
    opt.selected = option.value === current;
    node.append(opt);
  }
  node.addEventListener('change', () => onChange(node.value));
  wrap.append(node);
  return wrap;
}

/**
 * Mini-staafjes, geschaald op de hoogste waarde.
 *
 * Elk staafje draagt zijn eigen label: zonder dat is de tegel alleen een vorm en
 * kun je niet nagaan welk jaar welk getal had.
 */
export function miniBars(
  bars: { value: number; label: string }[],
  min = 8,
): string {
  const peak = Math.max(...bars.map((b) => b.value), 1);
  return bars
    .map(
      (b) =>
        `<div style="height:${Math.max(min, (b.value / peak) * 100).toFixed(0)}%"
              title="${escapeHtml(b.label)}"
              aria-label="${escapeHtml(b.label)}"></div>`,
    )
    .join('');
}

/** Gecentreerd voortschrijdend gemiddelde; gaten tellen niet als nul. */
export function rollingMean(values: (number | null)[], window: number): (number | null)[] {
  if (window <= 1) return values;
  const half = Math.floor(window / 2);
  return values.map((_, i) => {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(values.length - 1, i + half); j++) {
      const v = values[j];
      if (v !== null && v !== undefined) {
        sum += v;
        count += 1;
      }
    }
    return count >= Math.max(2, half) ? sum / count : null;
  });
}
